import {
  TBuyflowPathsByMarket,
  TCurrency,
  TIntlLocale,
  TLocale,
  TStore
} from 'types/common/common.types'
import { OrderPayloadBuilder } from 'types/order/order.types'

export const partyID = '36'

export const localeToStoreMap: Record<TLocale, TStore> = {
  br: 'BR',
  ar: 'AR',
  uy: 'UY',
  co: 'CO',
  cl: 'CL',
  ec: 'EC',
  pe: 'PE',
  mx: 'MX'
}

export const localeToIntlLocaleMap: Record<TLocale, TIntlLocale> = {
  br: 'pt-BR',
  ar: 'es-AR',
  uy: 'es-UY',
  co: 'es-CO',
  cl: 'es-CL',
  ec: 'es-EC',
  pe: 'es-PE',
  mx: 'es-MX'
}

export const localeToCurrencyMap: Record<TLocale, TCurrency> = {
  br: 'BRL',
  ar: 'ARS',
  uy: 'UYU',
  co: 'COP',
  cl: 'CLP',
  ec: 'USD',
  pe: 'PEN',
  mx: 'USD'
}

export const queryOptions = {
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  retry: (failureCount: number, error: unknown): boolean => {
    const status = (error as any)?.status
    if (status === 504) {
      return false
    }

    return failureCount < 2
  },
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000)
}

/**
 * Keys are saved in here cause they are used across the app.
 * NOTE: these keys are retro-compatible with the legacy buyflow and private web,
 * since they live under the same domain.
 * Keep them until we complete the migration with the login and selfcare sites.
 */
export const COOKIE_KEYS = {
  TOKEN: 'token',
  FORGEROCK_ID: 'forgerock_id',
  GA_CLIENT_ID: '_ga'
}

export const LOCAL_STORAGE_KEYS = {
  USER: 'user',
  FR_TOKEN: 'frToken',
  RENEW_ENTITLEMENTS: 'renewEntitlements',
  IS_OTT_TBX: 'isOttTbx',
  SESSION_TOKEN: 'sessionToken',
  ID_TOKEN: 'id_token',
  DEVICE_ID: 'deviceId',
  SUBSCRIPTION_ENTITLEMENTS: 'subscriptionEntitlements',
  USER_PRODUCT_LIST: 'userProductList',
  PROFILE: 'profile',
  UTM_PARAMS: 'utm-params',
  UTM_PARAMS_TIMESTAMP: 'utm-params-timestamp',
  DCLID: 'dclid',
  DCLID_TIMESTAMP: 'dclid-timestamp',
  GCLID: 'gclid',
  GCLID_TIMESTAMP: 'gclid-timestamp',
  CAMPAIGN_PARAMS_TTL_OVERRIDE: 'campaign-params-ttl-override',
  /** TCS-8180: persisted cookie-consent choices (LGPD/ANPD) */
  COOKIE_CONSENT: 'cookie-consent'
}

export const SESSION_STORAGE_KEYS = {
  USER_ID: 'user_uid',
  STATUS_ACCOUNT: 'statusAccount',
  PRE_SELECTED_PLAN_HOME: 'preselected_plan',
  CART_ID: 'cart_id',
  CREATE_ORDER_PAYLOAD: 'create_order_payload',
  ERROR_IN_PAYMENT_PAGE: 'error_in_payment_page',
  MAGENTO_STORE_CONFIG: 'magentoStoreConfig',
  OTP_TIMER_EXPIRY: 'otp_timer_expiry',
  EMAIL_CONFIRMATION_STATUS: 'validationCodeCheck',
  STORE: 'store',
  PIX_FLOW: 'PIX_FLOW',
  PIX_PAYMENT_STATUS: 'PIX_PAYMENT_STATUS',
  FROM_BUYFLOW_V1: 'from_buyflow_v1',
  VANITY: 'vanity',
  HAS_PENDING_PAYMENT: 'has_pending_payment',
  STATUS_BY_ACCOUNT: 'statusAccount',
  LAST_RESULT_FEEDBACK_ID: 'last_result_feedback_id',
  UTM_PARAMS: 'utm-params'
}

/**
 * Entire list of paths for Buyflow 2.0 pages, per market
 */
export const BUYFLOW_PATHS: TBuyflowPathsByMarket = {
  br: {
    HOME: 'home',
    REGISTRATION: 'cadastrar',
    PLANS: 'comprar/planos',
    EXTRAS: 'comprar/extras',
    OTP: 'comprar/otp',
    BILLING: 'comprar/dados',
    PAYMENT: 'comprar/pagamento',
    PAYMENT_PIX: 'comprar/pagamento/pix',
    PAYMENT_IN_ANALYSYS: 'comprar/processando-pagamento',
    ERROR: 'comprar/error',
    WELCOME: 'comprar/bem-vindo',
    LOGIN: 'acessar'
  },
  ssla: {
    HOME: 'home',
    REGISTRATION: 'registrar',
    PLANS: 'comprar/planes',
    EXTRAS: 'comprar/extras',
    OTP: 'comprar/otp',
    BILLING: 'comprar/datos',
    PAYMENT: 'comprar/pago',
    PAYMENT_PIX: 'comprar/pago/pix',
    PAYMENT_IN_ANALYSYS: 'comprar/procesando-pago',
    ERROR: 'comprar/error',
    WELCOME: 'comprar/bienvenido',
    /**
     * Since login still occurs in the legacy site, we replace the wildcard 'xx' with the locale
     */
    LOGIN: 'xx/acceder-xx'
  }
} as const

export const FREE_PLAN_PATH = 'granhermano/registrarse'
export const HOME_WEB_PRIVATE = 'home/main'

export type IBuyflowPathKey =
  | keyof typeof BUYFLOW_PATHS.br
  | keyof typeof BUYFLOW_PATHS.ssla

export const OTP_RESEND = {
  TIMER_DURATION_MS: 10 * 1000
}

export const TOAST_IDS = {
  TOAST_ERROR_PAYMENT: 'toast-error-payment',
  TOAST_ERROR_CODESHEET: 'toast-error-codesheet',
  TOAST_ERROR_CODEVALIDATION_EXPIREDTOKEN:
    'toast-error-codevalidation-expiredtoken',
  TOAST_ERROR_CODEVALIDATION_INVALIDTOKEN:
    'toast-error-codevalidation-invalidtoken',
  TOAST_SUCCESS_CODEVALIDATION: 'toast-success-codevalidation',
  TOAST_SUCCESS_PAYMENTPIX: 'toast-success-paymentpix',
  TOAST_SUCCESS_COPYPIXPAYMENTCODE: 'toast-success-copypixpaymentcode',
  TOAST_ERROR_APPLY_GIFT_CARD: 'toast-error-apply-gift-card',
  TOAST_ERROR_PLACE_ORDER: 'toast-error-place-order',
  TOAST_ERROR_REGISTER: 'toast-error-register',
  TOAST_LOADING_REGISTER: 'toast-loading-register',
  TOAST_ERROR_INVALID_CPF: 'toast-error-invalid-cpf',
  TOAST_ERROR_GFT1001: 'toast-error-gft1001',
  TOAST_ERROR_GFT1002: 'toast-error-gft1002',
  TOAST_ERROR_GFT1003: 'toast-error-gft1003',
  TOAST_ERROR_GFT1004: 'toast-error-gft1004',
  TOAST_ERROR_GFT1005: 'toast-error-gft1005',
  TOAST_ERROR_VCH1001: 'toast-error-vch1001',
  TOAST_ERROR_VCH1002: 'toast-error-vch1002',
  TOAST_ERROR_VCH1003: 'toast-error-vch1003',
  TOAST_ERROR_VCH1004: 'toast-error-vch1004',
  TOAST_ERROR_VCH1005: 'toast-error-vch1005',
  TOAST_ERROR_VCH1006: 'toast-error-vch1006'
}

export const RESULT_FEEDBACK_IDS = {
  ERROR_ATTEMPT_LIMIT: 'error-result-feedback-attempt-limit',
  ERROR_PIX_TIMEOUT: 'error-result-feedback-pix-timeout',
  ERROR_PIX_BLOCK: 'error-result-feedback-pix-block',
  ERROR_RESEND_LIMIT: 'error-result-feedback-resend-limit',
  ERROR_PAYMENT_UPDATE: 'error-result-feedback-payment-update',
  ERROR_MERCADO_PAGO_RETURN: 'error-result-feedback-mercado-pago-return',
  SUCCESS_WELCOME: 'success-result-feedback-welcome',
  ERROR_GENERIC: 'error-result-feedback-generic',
  ERROR_ORD0001: 'error-result-feedback-ord0001',
  ERROR_ORD0002: 'error-result-feedback-ord0002',
  ERROR_404: 'error-result-feedback-404',
  ERROR_500: 'error-result-feedback-500'
}

export const CREATE_ORDER_PAYLOAD_DEFAULT: OrderPayloadBuilder = {
  cartId: '',
  documentId: '',
  setBillingAddressOnCart: {
    addressValidated: false,
    city: '',
    countryCode: '',
    firstname: '',
    lastname: '',
    postcode: '',
    region: '',
    regionId: 0,
    street: [],
    telephone: ''
  },
  setPaymentMethodOnCart: {
    hasCard: false,
    hasCoupon: false,
    code: '',
    totalCoveredByAccountBalance: false
  }
}

interface ICountryFlag {
  name: string
  label: string
}

export const LOCALE_TO_COUNTRY_MAP: Record<TLocale, ICountryFlag> = {
  ar: {
    name: 'argentina',
    label: 'Argentina'
  },
  br: {
    name: 'brazil',
    label: 'Brasil'
  },
  cl: {
    name: 'chile',
    label: 'Chile'
  },
  co: {
    name: 'colombia',
    label: 'Colombia'
  },
  ec: {
    name: 'ecuador',
    label: 'Ecuador'
  },
  mx: {
    name: 'mexico',
    label: 'México'
  },
  pe: {
    name: 'peru',
    label: 'Perú'
  },
  uy: {
    name: 'uruguay',
    label: 'Uruguay'
  }
}
